import Debug from 'debug';
import { generateUniqueHash } from '../Hash/generator';

export interface DebugConfig {
	/**
	 * Globally changes logs to contain serialized arguments when logging method calls.
	 * This is useful when running in Node.js environment where the logs are collected into a file.
	 * In browser it's bad to serialize the arguments because browser allows to inspect the objects.
	 */
	serialize: boolean;
	logDuration: boolean;
}

let config = {
	serialize: false,
	logDuration: false,
};

function getDurationLog(start: Date) {
	if (config.logDuration) {
		const duration = new Date().getTime() - start.getTime();
		return ` (${duration}ms)`;
	}
}

export const createDebugMethod =
	(localDebug: Debug.IDebugger) => (originalMethod: (...args: any[]) => any, that: any, label: string, args: any[]) => {
		const invocationUid = generateUniqueHash(10);
		const start = new Date();

		localDebug(`invoking ${label} <${invocationUid}>`, ...serializeArgsIfEnabled(args));
		try {
			const returnValue = originalMethod.apply(that, args);
			if (returnValue instanceof Promise) {
				return returnValue
					.then((result: any) => {
						localDebug(`success ${label} <${invocationUid}>${getDurationLog(start)}`, result);
						return result;
					})
					.catch((error: any) => {
						localDebug(`fail ${label} <${invocationUid}>${getDurationLog(start)}`, error);
						throw error;
					});
			} else {
				localDebug(`success ${label} <${invocationUid}>${getDurationLog(start)}`, returnValue);
				return returnValue;
			}
		} catch (error) {
			localDebug(`fail ${label} <${invocationUid}>${getDurationLog(start)}`, error);
			throw error;
		}
	};

function serializeArgsIfEnabled(args: any[]) {
	return args.map((arg) => {
		if (typeof arg === 'object' && arg !== null && config.serialize) {
			return JSON.stringify(arg);
		} else {
			return arg;
		}
	});
}

/**
 * Add to a class method to debug it's calls
 *
 * For more information see https://www.npmjs.com/package/debug
 *
 * @param namespace Namespace of the debug logs. Run program with env variable DEBUG=<namespace> to enable the logs.
 */
export function debug(namespace: string, debugFactory = Debug) {
	const debugMethod = createDebugMethod(debugFactory(namespace));
	return function (_target: any, propertyKey: string, descriptor: PropertyDescriptor) {
		const originalMethod = descriptor.value;
		descriptor.value = function (...args: any[]) {
			return debugMethod(originalMethod, this, `${this.constructor.name}:${propertyKey}`, args);
		};
	};
}

export function getDebugConfig() {
	return config;
}

export function setDebugConfig(newConfig: Partial<DebugConfig>) {
	config = {
		...config,
		...newConfig,
	};
}
